import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, LogOut, RotateCcw, ExternalLink, Loader2, Upload, X, Plus, RefreshCw, FileText } from "lucide-react";
import { DEFAULT_CONTENT, CONTENT_FIELDS, resolveImg, useSite } from "@/lib/content";
import { compressImage } from "@/lib/imageCompress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lm_admin_token";

const GROUPS = [
  { title: "Accueil — Hero", fields: [
    { key: "hero_eyebrow", label: "Sur-titre", type: "text" },
    { key: "hero_title_1", label: "Titre — ligne 1", type: "text" },
    { key: "hero_title_2", label: "Titre — ligne 2 (italique)", type: "text" },
    { key: "hero_title_3", label: "Titre — ligne 3", type: "text" },
    { key: "hero_paragraph", label: "Paragraphe", type: "textarea" },
    { key: "hero_image", label: "Image de fond", type: "image" },
  ]},
  { title: "Accueil — Section « La maison »", fields: [
    { key: "intro_eyebrow", label: "Sur-titre", type: "text" },
    { key: "intro_title_1", label: "Titre — ligne 1", type: "text" },
    { key: "intro_title_2", label: "Titre — ligne 2", type: "text" },
    { key: "intro_title_italic", label: "Mot en italique", type: "text" },
    { key: "intro_paragraph", label: "Paragraphe", type: "textarea" },
    { key: "intro_hours", label: "Bloc horaires", type: "textarea" },
  ]},
  { title: "Images des plats", fields: [
    { key: "specialty_0_image", label: "Carte 01 — image", type: "image" },
    { key: "specialty_1_image", label: "Carte 02 — image", type: "image" },
    { key: "specialty_2_image", label: "Carte 03 — image", type: "image" },
    { key: "specialty_3_image", label: "Carte 04 (Fondues) — image", type: "image" },
    { key: "specialty_3_name", label: "Carte 04 — titre affiché", type: "text" },
  ]},
  { title: "Contact & réservation", fields: [
    { key: "phone_display", label: "Téléphone (affiché)", type: "text" },
    { key: "phone_tel", label: "Téléphone (international)", type: "text" },
    { key: "whatsapp_display", label: "WhatsApp (affiché)", type: "text" },
    { key: "whatsapp_number", label: "WhatsApp (digits only)", type: "text" },
    { key: "address", label: "Adresse", type: "text" },
    { key: "email", label: "E-mail", type: "text" },
  ]},
  { title: "Pied de page", fields: [
    { key: "footer_description", label: "Description", type: "textarea" },
  ]},
];

const SEO_GROUP = { title: "Référencement Google (SEO)", fields: [
  { key: "seo_title", label: "Titre onglet + Google (max 60 caractères)", type: "text" },
  { key: "seo_description", label: "Description Google (max 160 caractères)", type: "textarea" },
  { key: "seo_keywords", label: "Mots-clés (séparés par des virgules)", type: "textarea" },
  { key: "specialty_0_name", label: "Nom du plat 01 (pour Google)", type: "text" },
  { key: "specialty_1_name", label: "Nom du plat 02 (pour Google)", type: "text" },
  { key: "specialty_2_name", label: "Nom du plat 03 (pour Google)", type: "text" },
]};

const NAV_TABS = [
  { id: "content", label: "Contenu" },
  { id: "gallery", label: "Galerie" },
  { id: "menu", label: "Menu PDF" },
  { id: "seo", label: "SEO" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(Boolean(token));

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    (async () => {
      try { await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } }); }
      catch { localStorage.removeItem(TOKEN_KEY); setToken(""); }
      finally { setChecking(false); }
    })();
  }, [token]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C544F]"><Loader2 className="animate-spin" size={20} /></div>;
  }
  return token
    ? <Workspace token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />
    : <Login onSuccess={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
}

/* ============ LOGIN ============ */
function Login({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      toast.success("Connecté");
      onSuccess(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de se connecter");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F3E2] flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-[#FDFBF7] border border-[#E8E2D9] p-8 sm:p-10 shadow-sm" noValidate>
        <div className="lm-eyebrow">Espace privé</div>
        <h1 className="font-serif-display text-3xl sm:text-4xl mt-4 text-[#1A1A18] leading-tight">Administration</h1>
        <p className="mt-3 text-[13px] text-[#5C544F]">Réservé au propriétaire.</p>
        <div className="mt-8 space-y-6">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#5C544F]">Identifiant</span>
            <input type="text" data-testid="admin-username" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-2 w-full bg-transparent border-b border-[#1A1A18]/30 focus:border-[#8A1C29] outline-none py-2.5 text-[15px]" required />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#5C544F]">Mot de passe</span>
            <input type="password" data-testid="admin-password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 w-full bg-transparent border-b border-[#1A1A18]/30 focus:border-[#8A1C29] outline-none py-2.5 text-[15px]" required />
          </label>
        </div>
        <button type="submit" data-testid="admin-login-submit" disabled={loading} className="lm-btn-primary mt-10 w-full justify-center disabled:opacity-60">
          {loading ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </div>
  );
}

/* ============ WORKSPACE ============ */
function Workspace({ token, onLogout }) {
  const { content, gallery, refresh } = useSite();
  const [tab, setTab] = useState("content");
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => { setDraft(content); setDirty(false); }, [content]);

  const setField = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = CONTENT_FIELDS.reduce((acc, k) => {
        if (typeof draft[k] === "string" && draft[k] !== content[k]) acc[k] = draft[k];
        return acc;
      }, {});
      if (Object.keys(payload).length) {
        await axios.put(`${API}/content`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      await refresh();
      setPreviewKey((k) => k + 1);
      toast.success("Modifications publiées");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("Réinitialiser TOUT le contenu (textes, images, galerie) aux valeurs d'origine ?")) return;
    try {
      await axios.post(`${API}/content/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      setPreviewKey((k) => k + 1);
      toast.success("Contenu réinitialisé");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  const previewUrl = tab === "gallery" ? "/galerie" : "/";
  const dirtyOnEditingTab = dirty && (tab === "content" || tab === "seo");

  return (
    <div data-testid="admin-workspace" className="min-h-screen bg-[#F7F3E2] flex flex-col">
      <div className="sticky top-0 z-50 bg-[#1A1A18] text-[#FDFBF7]">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <span className="font-serif-display italic text-xl text-[#F3D1D6]">La Matze</span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-white/60">Administration</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setPreviewKey((k) => k + 1)} data-testid="admin-refresh" className="inline-flex items-center justify-center h-9 w-9 border border-white/25 hover:bg-white/10 transition-colors" aria-label="Rafraîchir l'aperçu">
              <RefreshCw size={14} />
            </button>
            <button type="button" onClick={resetAll} data-testid="admin-reset" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 border border-white/25 text-[11px] uppercase tracking-[0.14em] hover:bg-white/10 transition-colors">
              <RotateCcw size={12} /> Réinitialiser
            </button>
            <button type="button" onClick={save} disabled={saving || !dirtyOnEditingTab} data-testid="admin-save" className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#25D366] hover:bg-[#128C7E] text-white text-[11px] uppercase tracking-[0.14em] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Save size={12} /> {saving ? "…" : "Publier"}
            </button>
            <button type="button" onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center justify-center h-9 w-9 border border-white/25 hover:bg-white/10 transition-colors" aria-label="Se déconnecter">
              <LogOut size={13} />
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-6 flex gap-1 border-t border-white/10 overflow-x-auto">
          {NAV_TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`shrink-0 px-4 h-11 text-[11px] uppercase tracking-[0.18em] transition-colors ${tab === t.id ? "text-white border-b-2 border-[#F3D1D6]" : "text-white/50 hover:text-white/80"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-[minmax(0,1fr)_460px] xl:grid-cols-[minmax(0,1fr)_520px]">
        {/* Preview */}
        <div className="hidden lg:block bg-[#1A1A18] border-r border-[#E8E2D9]/30 relative">
          <iframe
            key={previewKey}
            title="Aperçu du site"
            src={previewUrl}
            data-testid="admin-preview"
            className="w-full h-[calc(100vh-100px)] bg-white"
          />
          <a href={previewUrl} target="_blank" rel="noreferrer noopener" className="absolute top-3 right-3 inline-flex items-center gap-1.5 h-8 px-3 bg-black/70 text-white text-[10px] uppercase tracking-[0.16em] hover:bg-black/90">
            <ExternalLink size={11} /> Ouvrir
          </a>
        </div>

        {/* Editor pane */}
        <div className="bg-[#FDFBF7] overflow-y-auto lg:h-[calc(100vh-100px)]">
          <div className="p-5 sm:p-7 lg:p-8">
            {tab === "content" && (
              <ContentEditor draft={draft} setField={setField} token={token} onUpload={() => setPreviewKey((k) => k + 1)} />
            )}
            {tab === "gallery" && (
              <GalleryEditor token={token} gallery={gallery} refresh={refresh} setPreviewKey={setPreviewKey} />
            )}
            {tab === "menu" && (
              <MenuEditor token={token} content={content} refresh={refresh} setPreviewKey={setPreviewKey} />
            )}
            {tab === "seo" && (
              <SeoEditor draft={draft} setField={setField} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ CONTENT ============ */
function ContentEditor({ draft, setField, token, onUpload }) {
  return (
    <div className="space-y-8">
      <p className="text-[13px] text-[#5C544F] leading-relaxed">
        Modifiez le texte, remplacez les images (JPG/PNG/WEBP). Les grosses photos sont automatiquement compressées avant l&rsquo;envoi. Puis cliquez <strong>Publier</strong>.
      </p>
      {GROUPS.map((g, gi) => (
        <section key={g.title}>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-serif-display italic text-xl text-[#8A1C29]">{String(gi + 1).padStart(2, "0")}</span>
            <h2 className="font-serif-display text-lg sm:text-xl text-[#1A1A18]">{g.title}</h2>
          </div>
          <div className="border border-[#E8E2D9] p-4 sm:p-5 space-y-5">
            {g.fields.map((f) => (
              <FieldRow key={f.key} field={f} value={draft[f.key] || ""} onChange={(v) => setField(f.key, v)} token={token} onUpload={onUpload} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ============ SEO ============ */
function SeoEditor({ draft, setField }) {
  const titleLen = (draft.seo_title || "").length;
  const descLen = (draft.seo_description || "").length;
  return (
    <div className="space-y-8">
      <div>
        <div className="lm-eyebrow" style={{ color: "#8A1C29" }}>Référencement</div>
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1A1A18] mt-3 leading-tight">
          Comment le site apparaît sur <em className="italic text-[#8A1C29]">Google</em>.
        </h2>
        <p className="mt-3 text-[13px] text-[#5C544F] leading-relaxed">
          Ces champs ne sont pas visibles sur le site. Ils sont lus par Google et
          les moteurs de recherche pour classer et afficher La Matze dans les résultats.
        </p>
      </div>

      {/* Live search preview */}
      <div className="border border-[#E8E2D9] p-5 bg-white">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#5C544F] mb-3">Aperçu Google</div>
        <div className="text-[13px] text-[#4d5156] mb-1">restaurant-la-matze.ch › Genève</div>
        <div className="text-[18px] leading-snug text-[#1a0dab] hover:underline cursor-pointer max-w-xl truncate">
          {draft.seo_title || DEFAULT_CONTENT.seo_title}
        </div>
        <div className="mt-1.5 text-[13px] leading-snug text-[#4d5156] max-w-xl">
          {(draft.seo_description || DEFAULT_CONTENT.seo_description).slice(0, 160)}
        </div>
      </div>

      <div className="border border-[#E8E2D9] p-4 sm:p-5 space-y-5">
        {SEO_GROUP.fields.map((f) => (
          <div key={f.key}>
            <FieldRow field={f} value={draft[f.key] || ""} onChange={(v) => setField(f.key, v)} />
            {f.key === "seo_title" && (
              <div className={`mt-1 text-[10px] uppercase tracking-[0.14em] ${titleLen > 60 ? "text-[#8A1C29]" : "text-[#5C544F]"}`}>
                {titleLen} / 60 caractères
              </div>
            )}
            {f.key === "seo_description" && (
              <div className={`mt-1 text-[10px] uppercase tracking-[0.14em] ${descLen > 160 ? "text-[#8A1C29]" : "text-[#5C544F]"}`}>
                {descLen} / 160 caractères
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ MENU PDF ============ */
function MenuEditor({ token, content, refresh, setPreviewKey }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const currentUrl = resolveImg(content.menu_pdf_url || "/menu-la-matze.pdf");

  const upload = async (file) => {
    if (!file) return;
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (ext !== "pdf") {
      toast.error("Le fichier doit être un PDF.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await axios.put(`${API}/content`, { menu_pdf_url: data.url }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      setPreviewKey((k) => k + 1);
      toast.success("Menu PDF mis à jour");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur d'envoi"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const reset = async () => {
    if (!window.confirm("Revenir au menu d'origine ?")) return;
    try {
      await axios.put(`${API}/content`, { menu_pdf_url: DEFAULT_CONTENT.menu_pdf_url }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      setPreviewKey((k) => k + 1);
      toast.success("Menu réinitialisé");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="lm-eyebrow" style={{ color: "#8A1C29" }}>Menu</div>
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1A1A18] mt-3 leading-tight">
          Le PDF affiché quand un client clique sur <em className="italic text-[#8A1C29]">« Menu »</em>.
        </h2>
      </div>

      <div className="border border-[#E8E2D9] bg-white p-5 flex items-start gap-4">
        <div className="w-14 h-16 flex items-center justify-center bg-[#8A1C29] text-white shrink-0">
          <FileText size={22} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#5C544F]">Menu actuel</div>
          <div className="font-serif-display text-lg text-[#1A1A18] mt-1 truncate">
            {content.menu_pdf_url === DEFAULT_CONTENT.menu_pdf_url ? "Menu d'origine (statique)" : "Menu personnalisé"}
          </div>
          <a href={currentUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1.5 mt-2 text-[11px] uppercase tracking-[0.14em] text-[#8A1C29] hover:text-[#6B141E]">
            <ExternalLink size={12} /> Ouvrir le PDF actuel
          </a>
        </div>
      </div>

      <div className="border-2 border-dashed border-[#8A1C29]/30 bg-[#F3EFE9] p-6 text-center">
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="admin-menu-file" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="admin-menu-upload" className="inline-flex items-center gap-2 h-11 px-6 bg-[#8A1C29] text-white text-[12px] uppercase tracking-[0.14em] font-semibold hover:bg-[#6B141E] transition-colors disabled:opacity-60">
          <Upload size={14} /> {uploading ? "Envoi…" : "Remplacer par un nouveau PDF"}
        </button>
        <p className="mt-3 text-[11px] text-[#5C544F]">
          Format PDF · max 25 Mo. Le nouveau menu est publié immédiatement.
        </p>
      </div>

      {content.menu_pdf_url !== DEFAULT_CONTENT.menu_pdf_url && (
        <button type="button" onClick={reset} data-testid="admin-menu-reset" className="text-[11px] uppercase tracking-[0.16em] text-[#8A1C29] hover:text-[#6B141E]">
          Revenir au menu d&rsquo;origine
        </button>
      )}
    </div>
  );
}

/* ============ Field row ============ */
function FieldRow({ field, value, onChange, token, onUpload }) {
  const original = DEFAULT_CONTENT[field.key];
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C544F]">{field.label}</span>
      {field.type === "textarea" ? (
        <textarea rows={field.key.includes("paragraph") || field.key.includes("description") ? 4 : 3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`admin-field-${field.key}`} className="mt-2 w-full bg-white border border-[#E8E2D9] focus:border-[#8A1C29] outline-none px-3 py-2 text-[14px] text-[#1A1A18] resize-y" />
      ) : field.type === "image" ? (
        <ImageField value={value} onChange={onChange} token={token} onUpload={onUpload} testid={`admin-field-${field.key}`} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`admin-field-${field.key}`} className="mt-2 w-full bg-white border border-[#E8E2D9] focus:border-[#8A1C29] outline-none px-3 py-2 text-[14px] text-[#1A1A18]" />
      )}
      {value !== original && field.type !== "image" ? (
        <button type="button" onClick={() => onChange(original)} className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-[#8A1C29] hover:text-[#6B141E]">
          Rétablir l&rsquo;original
        </button>
      ) : null}
    </label>
  );
}

/* ============ Image upload widget (auto-compresses phone photos) ============ */
function ImageField({ value, onChange, token, onUpload, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      setStatus("Préparation…");
      const compressed = await compressImage(file, { maxWidth: 2400, quality: 0.85 });
      if (compressed !== file) {
        setStatus(`Compressé ${(file.size / 1024 / 1024).toFixed(1)} → ${(compressed.size / 1024 / 1024).toFixed(1)} Mo`);
      } else {
        setStatus("Envoi…");
      }
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      if (onUpload) onUpload();
      toast.success("Image téléchargée");
      setStatus("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec du téléchargement"); setStatus(""); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="mt-2">
      <div className="flex items-start gap-3">
        {value ? (
          <div className="border border-[#E8E2D9] bg-[#F3EFE9] p-1.5 shrink-0">
            <img src={resolveImg(value)} alt="" className="h-20 w-28 object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
          </div>
        ) : (
          <div className="h-20 w-28 border border-dashed border-[#8A1C29]/30 flex items-center justify-center text-[#8A1C29]/40 text-[10px] uppercase">vide</div>
        )}
        <div className="flex-1 flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid={`${testid}-file`} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={testid} className="inline-flex items-center justify-center gap-2 h-9 px-4 bg-[#1A1A18] text-[#FDFBF7] text-[11px] uppercase tracking-[0.14em] hover:bg-[#8A1C29] transition-colors disabled:opacity-60">
            <Upload size={12} /> {uploading ? "Envoi…" : "Choisir une image"}
          </button>
          <span className="text-[10px] text-[#5C544F]">
            {status || "JPG · PNG · WEBP · compressé automatiquement"}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ============ GALLERY (upload + delete) ============ */
function GalleryEditor({ token, gallery, refresh, setPreviewKey }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");

  const addFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      setStatus("Préparation…");
      const compressed = await compressImage(file, { maxWidth: 2400, quality: 0.85 });
      if (compressed !== file) {
        setStatus(`Compressé ${(file.size / 1024 / 1024).toFixed(1)} → ${(compressed.size / 1024 / 1024).toFixed(1)} Mo`);
      }
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await axios.post(`${API}/admin/gallery`, { url: data.url }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      setPreviewKey((k) => k + 1);
      toast.success("Photo ajoutée à la galerie");
      setStatus("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); setStatus(""); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async (index) => {
    if (!window.confirm("Retirer cette photo de la galerie ?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${index}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh();
      setPreviewKey((k) => k + 1);
      toast.success("Photo retirée");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-serif-display italic text-xl text-[#8A1C29]">01</span>
        <h2 className="font-serif-display text-lg sm:text-xl text-[#1A1A18]">Galerie photos</h2>
      </div>
      <p className="text-[13px] text-[#5C544F] leading-relaxed">
        Ajoutez ou retirez des photos. Les grosses images sont compressées automatiquement.
      </p>

      <div className="mt-6 border-2 border-dashed border-[#8A1C29]/30 bg-[#F3EFE9] p-6 text-center">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => addFile(e.target.files?.[0])} className="hidden" data-testid="admin-gallery-file" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="admin-gallery-add" className="inline-flex items-center gap-2 h-11 px-6 bg-[#8A1C29] text-white text-[12px] uppercase tracking-[0.14em] font-semibold hover:bg-[#6B141E] transition-colors disabled:opacity-60">
          <Plus size={14} /> {uploading ? "Envoi…" : "Ajouter une photo"}
        </button>
        <p className="mt-3 text-[11px] text-[#5C544F]">
          {status || `${gallery.length} photo${gallery.length > 1 ? "s" : ""} · JPG/PNG/WEBP`}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {gallery.map((src, i) => (
          <div key={`${src}-${i}`} data-testid={`admin-gallery-item-${i}`} className="relative group border border-[#E8E2D9] overflow-hidden aspect-[4/3]">
            <img src={resolveImg(src)} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
            <button type="button" onClick={() => remove(i)} data-testid={`admin-gallery-remove-${i}`} className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-7 w-7 bg-[#8A1C29] hover:bg-[#6B141E] text-white transition-colors opacity-0 group-hover:opacity-100" aria-label="Retirer">
              <X size={13} />
            </button>
          </div>
        ))}
        {gallery.length === 0 && (
          <div className="col-span-full text-center py-10 text-[#5C544F] text-[13px]">
            Aucune photo — ajoutez la première ci-dessus.
          </div>
        )}
      </div>
    </div>
  );
}
